////// Lib. //////
//////////////////

import { gsap } from "gsap";

///// Assets /////
//////////////////

// Share of the viewport the enlarged image is fitted into.
const FIT = 0.8;

const lerp = (a, b, t) => a + (b - a) * t;

// Viewer class //
//////////////////

export default class WorkStreamViewer {
  /////////// Constructor ////////////
  ////////////////////////////////////

  // `getTwins` receives the clicked item box and returns every box rendering the same
  // item (the loop renders each set twice), the clicked one included.
  constructor({ refs, getTwins, onOpen, onClose, onFinishClose, duration = 0.6, blurTargets = [], blur = 12 }) {
    this.ref = refs;
    this._getTwins = getTwins;
    this._onOpen = onOpen;
    this._onClose = onClose;
    this._onFinishClose = onFinishClose;
    this._duration = duration;
    this._blurTargets = blurTargets;
    this._blur = blur;
    this._blurProxy = { value: 0 };

    this._twins = [];
    this._items = [];
    this._lastCenter = null;
    this._target = null;
    this._ratio = 1;
    this._tween = null;
    this._trackFn = null;
    this._trigger = null;
    this._busy = false;
    this._closing = false;

    this._onCloseClick = () => this.close();
    this._onKeyDown = (e) => {
      if (e.key === "Escape") this.close();
    };
    this._onRootClick = (e) => {
      if (!this.isOpen || this.ref.viewerFigure?.contains(e.target)) return;
      this.close();
    };

    this.ref.viewerClose?.addEventListener("click", this._onCloseClick);
    this.ref.viewerRoot?.addEventListener("click", this._onRootClick);
  }

  /////////// Public /////////////////
  ////////////////////////////////////

  get isOpen() {
    return this._trigger !== null;
  }

  open(button) {
    if (this._busy || this.isOpen) return;

    const box = button.closest(".work-stream__item-box");
    const root = this.ref.viewerRoot;
    if (!box || !root) return;

    this._trigger = button;
    this._twins = this._getTwins(box);
    this._items = this._twins.map((el) => el.closest(".work-stream__item")).filter(Boolean);

    const first = box.getBoundingClientRect();
    if (!first.width || !first.height) return this._reset();

    this._lastCenter = { x: first.left + first.width / 2, y: first.top + first.height / 2 };
    this._ratio = first.width / first.height;
    this._target = this._targetRect();

    this._fillSources(box, button);
    this._fillCaption(button);

    root.classList.remove("hidden");
    root.setAttribute("aria-hidden", "false");

    gsap.set(this.ref.viewerFigure, { width: this._target.width, height: this._target.height });
    this._paint(0);

    this._items.forEach((el) => {
      el.style.opacity = "0";
    });

    window.addEventListener("keydown", this._onKeyDown);
    this._startTracking();
    this._onOpen?.();

    this._flip(0, 1);
    this._fadeChrome(1);
    this._tweenBlur(this._blur);
  }

  close({ immediate = false } = {}) {
    if (!this.isOpen || this._closing) return;

    this._closing = true;
    window.removeEventListener("keydown", this._onKeyDown);
    this._onClose?.();

    if (immediate) {
      this._tween?.kill();
      this._finishClose();
      return;
    }

    this._fadeChrome(0);
    this._tweenBlur(0);
    this._flip(1, 0, () => this._finishClose());
  }

  destroy() {
    this.close({ immediate: true });
    this.ref.viewerClose?.removeEventListener("click", this._onCloseClick);
    this.ref.viewerRoot?.removeEventListener("click", this._onRootClick);
  }

  /////////// Private ////////////////
  ////////////////////////////////////

  // Fitted, centred rect for the enlarged image.
  _targetRect() {
    const maxW = window.innerWidth * FIT;
    const maxH = window.innerHeight * FIT;

    let width = maxW;
    let height = width / this._ratio;
    if (height > maxH) {
      height = maxH;
      width = height * this._ratio;
    }

    return { left: (window.innerWidth - width) / 2, top: (window.innerHeight - height) / 2, width, height };
  }

  // Resolves the item's current on-screen rect across the two twin nodes. The layer
  // transform wraps by exactly one set height, so at a wrap the tracked node jumps off
  // while its twin arrives in its place — picking the node nearest the previously
  // tracked centre follows the item continuously across that boundary.
  _liveRect() {
    let best = null;
    let bestDistance = Infinity;

    this._twins.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const distance = Math.hypot(rect.left + rect.width / 2 - this._lastCenter.x, rect.top + rect.height / 2 - this._lastCenter.y);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = rect;
      }
    });

    this._lastCenter = { x: best.left + best.width / 2, y: best.top + best.height / 2 };
    return best;
  }

  // Keeps the tracked centre current while the viewer sits open, so a wrap that happens
  // in the meantime is already accounted for when the fly-back starts.
  _startTracking() {
    if (this._trackFn) return;
    this._trackFn = () => this._liveRect();
    gsap.ticker.add(this._trackFn);
  }

  _stopTracking() {
    if (!this._trackFn) return;
    gsap.ticker.remove(this._trackFn);
    this._trackFn = null;
  }

  // p = 0 sits exactly on the item's live rect, p = 1 on the centred target rect.
  // The live rect is re-read every frame, so the stream can keep moving underneath
  // without the animation ever losing its anchor.
  _paint(p) {
    const live = this._liveRect();
    const target = this._target;

    const scale = lerp(live.width / target.width, 1, p);
    const x = lerp(live.left, target.left, p);
    const y = lerp(live.top, target.top, p);

    this.ref.viewerFigure.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  }

  _flip(from, to, onComplete) {
    this._tween?.kill();
    this._busy = true;

    const proxy = { p: from };
    this._tween = gsap.to(proxy, {
      p: to,
      duration: this._duration,
      ease: "power3.inOut",
      onUpdate: () => this._paint(proxy.p),
      onComplete: () => {
        this._tween = null;
        this._busy = false;
        onComplete?.();
      },
    });
  }

  _fadeChrome(to) {
    gsap.to([this.ref.viewerCaption, this.ref.viewerCaptionBottom, this.ref.viewerClose], {
      opacity: to,
      duration: this._duration * 0.6,
      ease: "power2.out",
      overwrite: "auto",
    });
  }

  // A real filter on the stream rather than backdrop-filter on the overlay: the scene's
  // mix-blend-multiply layers and the bg-grid wrapper are separate compositing groups that
  // a backdrop sample fails to capture, leaving them sharp on top of the dimmed backdrop.
  _tweenBlur(to) {
    if (!this._blurTargets.length) return;

    gsap.to(this._blurProxy, {
      value: to,
      duration: this._duration * 0.6,
      ease: "power2.out",
      overwrite: true,
      onUpdate: () => this._applyBlur(this._blurProxy.value),
    });
  }

  _applyBlur(value) {
    const filter = value > 0.05 ? `blur(${value}px)` : "";
    this._blurTargets.forEach((el) => {
      el.style.filter = filter;
    });
  }

  // The base layer reuses whatever the stream already painted, so the first frame of
  // the animation is never blank; the hi-res layer crossfades in once it has decoded.
  _fillSources(box, button) {
    const streamImg = box.querySelector("img");
    const base = this.ref.viewerImgBase;
    const hi = this.ref.viewerImgHi;

    if (base && streamImg) {
      base.srcset = "";
      base.src = streamImg.currentSrc || streamImg.src;
    }

    const source = button.dataset.viewerSrc;
    if (!hi || !source) return;

    gsap.set(hi, { opacity: 0 });
    hi.alt = button.dataset.title || "";
    hi.srcset = button.dataset.viewerSrcset || "";
    hi.src = source;

    hi.decode?.()
      .then(() => {
        if (this.isOpen && hi.src === source) gsap.to(hi, { opacity: 1, duration: 0.4, ease: "power2.out" });
      })
      .catch(() => {});
  }

  _fillCaption(button) {
    const { viewerTitle, viewerLink } = this.ref;
    if (viewerTitle) viewerTitle.textContent = button.dataset.title || "";

    if (viewerLink) {
      const link = button.dataset.projectLink || "";
      viewerLink.href = link;
      viewerLink.classList.toggle("hidden", !link);
    }
  }

  _finishClose() {
    this._items.forEach((el) => {
      el.style.opacity = "";
    });

    gsap.killTweensOf(this._blurProxy);
    this._blurProxy.value = 0;
    this._applyBlur(0);

    const root = this.ref.viewerRoot;
    root?.classList.add("hidden");
    root?.setAttribute("aria-hidden", "true");

    gsap.set([this.ref.viewerCaption, this.ref.viewerCaptionBottom, this.ref.viewerClose], { opacity: 0 });

    this._trigger?.focus?.({ preventScroll: true });
    this._onFinishClose?.();
    this._reset();
  }

  _reset() {
    this._stopTracking();
    this._busy = false;
    this._closing = false;
    this._trigger = null;
    this._twins = [];
    this._items = [];
    this._lastCenter = null;
  }
}
